import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { api, errMsg } from '../lib/api';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match. Please retype them.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/auth/reset-password', { token, new_password: password });
      toast.success(data.message);
      navigate('/login');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center px-4 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(14,165,164,0.10),transparent_55%),radial-gradient(900px_circle_at_90%_10%,rgba(253,231,179,0.30),transparent_50%)]">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <span className="inline-flex h-12 w-12 rounded-2xl bg-[hsl(var(--primary))] text-white items-center justify-center shadow-[var(--shadow-soft)]">
            <Wrench className="h-6 w-6" />
          </span>
          <h1 className="text-2xl font-semibold font-display">Choose a new password</h1>
        </div>
        <Card className="rounded-2xl shadow-[var(--shadow-card)] border-black/5">
          <CardContent className="pt-6">
            {!token ? (
              <p className="text-sm text-center">
                This reset link is missing its code. Please open the link from your email again, or{' '}
                <Link to="/forgot-password" className="font-medium text-[hsl(var(--primary))]">request a new one</Link>.
              </p>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <Input data-testid="reset-password-input" type="password" required minLength={8}
                  placeholder="New password (min 8 characters)"
                  value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl" />
                <Input data-testid="reset-confirm-input" type="password" required minLength={8}
                  placeholder="Retype new password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12 rounded-xl" />
                <Button data-testid="reset-submit-button" type="submit" disabled={busy} className="w-full h-12 rounded-xl text-base">
                  {busy ? 'Saving…' : 'Set new password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        <p className="text-center text-sm text-[hsl(var(--muted-foreground))]">
          <Link to="/login" className="font-medium text-[hsl(var(--primary))]">Back to log in</Link>
        </p>
      </div>
    </div>
  );
}
